// Read projection of `RequestView.boxes` for the items tab, plus the
// optional `#1178` regroup edit affordance for the assigned logist on
// `IN_WORK` STANDARD requests. The toggle and editor live per-box
// because each `RequestBox.updatedAt` is the per-box optimistic-
// concurrency token; staging is also per-box (cross-box is not in
// scope per the issue contract).
//
// Read shell (BoxBlock + BoxPropertiesDesktop/Mobile + styles) stays
// identical; item rendering is forwarded to `./BoxItems.js`.

import { useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import type { RequestKind, RequestStatus } from '@magimp/shared';
import { Button, useNarrow } from '../../../../ui/index.js';
import { useAuthStore } from '../../../../stores/auth.js';
import { canRegroupBoxItems } from '../../role.js';
import { formatVolume, formatWeight } from '../../format.js';
import type { RequestBoxView } from '../../types.js';
import { BoxItemsTableDesktop, isVisibleBoxItem, renderMobileItems } from './BoxItems.js';
import { BoxRegroupEditor } from './BoxRegroupEditor.js';

export interface RequestBoxesViewProps {
  requestId: string;
  boxes: ReadonlyArray<RequestBoxView>;
  /**
   * Party + status context for the regroup edit gate (#1178). When
   * absent or when `canRegroupBoxItems` returns false, the view stays
   * fully read-only (legacy behavior). Threaded through from the
   * drawer so the call site keeps the single source of truth for
   * actor / managerId / logistId.
   */
  regroup?: {
    status: RequestStatus;
    kind: RequestKind;
    managerId: string;
    logistId: string | null;
  };
}

export function RequestBoxesView({ requestId, boxes, regroup }: RequestBoxesViewProps) {
  const { t } = useTranslation(['requests']);
  const narrow = useNarrow();
  if (boxes.length === 0) return null;
  return (
    <div style={{ marginTop: 24 }}>
      <h3 style={sectionTitleStyle}>{t('requests:draft.boxes.sectionTitle')}</h3>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 12 }}>
        {boxes.map((b, idx) => (
          <li key={b.id} style={boxCardStyle}>
            <BoxBlock requestId={requestId} box={b} index={idx} narrow={narrow} regroup={regroup} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function BoxBlock({
  requestId,
  box,
  index,
  narrow,
  regroup,
}: {
  requestId: string;
  box: RequestBoxView;
  index: number;
  narrow: boolean;
  regroup: RequestBoxesViewProps['regroup'];
}) {
  const { t } = useTranslation(['requests']);
  const permissions = useAuthStore((s) => s.permissions);
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const visibleItems = box.items.filter(isVisibleBoxItem);

  const canEdit =
    regroup !== undefined &&
    canRegroupBoxItems(
      permissions,
      regroup.status,
      regroup.kind,
      {
        actorUserId: userId,
        managerId: regroup.managerId,
        logistId: regroup.logistId,
      },
      box.updatedAt,
    );

  const [editing, setEditing] = useState(false);

  return (
    <section aria-label={t('requests:draft.boxes.boxLabel', { index: index + 1 })}>
      <header style={boxHeadingStyle}>
        <span>{t('requests:draft.boxes.boxLabel', { index: index + 1 })}</span>
        {canEdit && !editing && (
          <Button
            kind="quiet"
            size="sm"
            onClick={() => setEditing(true)}
            data-testid={`box-regroup-edit-${box.id}`}
          >
            {t('requests:boxRegroup.action.edit')}
          </Button>
        )}
      </header>
      {narrow ? (
        <BoxPropertiesMobile box={box} index={index} />
      ) : (
        <BoxPropertiesDesktop box={box} index={index} />
      )}
      {visibleItems.length > 0 && (
        <div style={contentsSectionStyle}>
          <div style={contentsTitleStyle}>{t('requests:draft.boxes.contents')}</div>
          {narrow ? (
            <ul
              style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 6 }}
              data-testid={`box-${box.id}-items-mobile`}
            >
              {renderMobileItems(requestId, box.id, visibleItems)}
            </ul>
          ) : (
            <BoxItemsTableDesktop requestId={requestId} boxId={box.id} items={visibleItems} />
          )}
        </div>
      )}
      {editing && canEdit && box.updatedAt !== undefined && (
        <BoxRegroupEditor
          requestId={requestId}
          boxId={box.id}
          items={visibleItems}
          boxUpdatedAt={box.updatedAt}
          onClose={() => setEditing(false)}
        />
      )}
    </section>
  );
}

function BoxPropertiesDesktop({ box, index }: { box: RequestBoxView; index: number }) {
  const { t } = useTranslation(['requests']);
  return (
    <div style={boxPropsGridStyle}>
      <div style={propHeaderCellStyle}>#</div>
      <div style={propHeaderCellStyle}>{t('requests:draft.boxes.count')}</div>
      <div style={{ ...propHeaderCellStyle, justifyContent: 'flex-end' }}>
        {t('requests:draft.boxes.weightKg')}
      </div>
      <div style={{ ...propHeaderCellStyle, justifyContent: 'flex-end' }}>
        {t('requests:draft.boxes.volumeM3')}
      </div>
      <div style={{ ...propHeaderCellStyle, justifyContent: 'flex-end' }}>
        {t('requests:common.weight')}
      </div>
      <div style={{ ...propHeaderCellStyle, justifyContent: 'flex-end' }}>
        {t('requests:common.volume')}
      </div>
      <div style={propCellStyle}>{index + 1}</div>
      <div style={propCellStyle}>{box.count}</div>
      <div style={{ ...propCellStyle, justifyContent: 'flex-end' }}>
        {formatWeight(box.weightKg)}
      </div>
      <div style={{ ...propCellStyle, justifyContent: 'flex-end' }}>
        {formatVolume(box.volumeM3)}
      </div>
      <div style={{ ...propCellStyle, justifyContent: 'flex-end' }}>
        {formatWeight(box.weightTotalKg)}
      </div>
      <div style={{ ...propCellStyle, justifyContent: 'flex-end' }}>
        {formatVolume(box.volumeTotalM3)}
      </div>
    </div>
  );
}

function BoxPropertiesMobile({ box, index }: { box: RequestBoxView; index: number }) {
  const { t } = useTranslation(['requests']);
  return (
    <div style={{ display: 'grid', gap: 4, fontSize: 12, color: 'var(--fg-2)' }}>
      <div>
        <span style={mobilePropLabelStyle}>#</span> {index + 1} ·{' '}
        <span style={mobilePropLabelStyle}>{t('requests:draft.boxes.count')}</span> {box.count}
      </div>
      <div>
        <span style={mobilePropLabelStyle}>{t('requests:draft.boxes.weightKg')}</span>{' '}
        {formatWeight(box.weightKg)} ·{' '}
        <span style={mobilePropLabelStyle}>{t('requests:draft.boxes.volumeM3')}</span>{' '}
        {formatVolume(box.volumeM3)}
      </div>
      <div>
        <span style={mobilePropLabelStyle}>{t('requests:common.weight')}</span>{' '}
        {formatWeight(box.weightTotalKg)} ·{' '}
        <span style={mobilePropLabelStyle}>{t('requests:common.volume')}</span>{' '}
        {formatVolume(box.volumeTotalM3)}
      </div>
    </div>
  );
}

const sectionTitleStyle: CSSProperties = {
  fontSize: 14,
  fontWeight: 600,
  margin: '0 0 8px',
  color: 'var(--fg-1)',
};

const boxCardStyle: CSSProperties = {
  border: '1px solid var(--border-2)',
  borderRadius: 6,
  padding: 10,
  background: 'var(--bg-2)',
};

const boxHeadingStyle: CSSProperties = {
  fontWeight: 600,
  fontSize: 13,
  marginBottom: 8,
  color: 'var(--fg-1)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
};

const boxPropsGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '40px 80px 130px 130px 110px 120px',
  borderTop: '1px solid var(--border-2)',
};

const propHeaderCellStyle: CSSProperties = {
  padding: '0 10px',
  height: 28,
  display: 'flex',
  alignItems: 'center',
  fontSize: 10,
  letterSpacing: '.08em',
  textTransform: 'uppercase',
  fontWeight: 600,
  color: 'var(--fg-3)',
  borderBottom: '1px solid var(--border-1)',
};

const propCellStyle: CSSProperties = {
  padding: '6px 10px',
  display: 'flex',
  alignItems: 'center',
  borderBottom: '1px solid var(--border-2)',
  fontSize: 13,
  fontVariantNumeric: 'tabular-nums',
};

const contentsSectionStyle: CSSProperties = {
  marginTop: 10,
};

const contentsTitleStyle: CSSProperties = {
  fontSize: 10,
  letterSpacing: '.08em',
  textTransform: 'uppercase',
  fontWeight: 600,
  color: 'var(--fg-3)',
  margin: '0 0 6px',
};

const mobilePropLabelStyle: CSSProperties = {
  fontSize: 10,
  letterSpacing: '.08em',
  textTransform: 'uppercase',
  color: 'var(--fg-3)',
};
